import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './CarruselCard.css';

export default function CarruselCard({ title, items = [], categoryRoute }) {
  const containerRef = useRef(null);

  // visibleCount por ancho REAL del carrusel (no de la ventana)
  const [visibleCount, setVisibleCount] = useState(5);

  // detectamos si estamos en modo "1x" para activar el spacer
  const isOneUp = visibleCount === 1;

  useEffect(() => {
    if (!containerRef.current) return;

    const computeVC = (w) => {
      // ▸ Ajustá umbrales si tu layout lo pide:
      //  >=1320 → 5, >=1080 → 4, >=840 → 3, >=600 → 2, else → 1
      if (w >= 1320) return 5;
      if (w >= 1080) return 4;
      if (w >= 840)  return 3;
      if (w >= 600)  return 2;
      return 1;
    };

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 1200;
      const next = computeVC(w);
      setVisibleCount((prev) => (prev === next ? prev : next));
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Pool: horizontales primero; si no hay, usa todo
  const pool = useMemo(() => {
    const base = Array.isArray(items) ? items : [];
    const horizontals = base.filter((it) => (it?.ratio ?? 1) >= 1);
    return horizontals.length ? horizontals : base;
  }, [items]);

  // 7 aleatorias + "Ver más" al final
  const deck = useMemo(() => {
    const arr = [...pool];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const take = arr.slice(0, Math.min(7, arr.length));
    return [...take, { id: '__more__', type: 'more' }];
  }, [pool]);

  const [index, setIndex] = useState(0);
  const total = deck.length;                          // p.ej., 8
  const maxIndex = Math.max(0, total - visibleCount); // asegura alcanzar "Ver más"

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  const canPrev = index > 0;
  const canNext = index < maxIndex;

  const handlePrev = () => canPrev && setIndex((i) => i - 1);
  const handleNext = () => canNext && setIndex((i) => i + 1);

  const visibleItems = deck.slice(index, index + visibleCount);

  const preventContextMenu = (e) => e.preventDefault();

  // ¿estamos mostrando "Ver más" en 1×?
  const showingMoreOneUp = isOneUp && visibleItems[0]?.type === 'more';

  return (
    <section className="carrusel-card">
      <div className="carrusel-header">
        <h2 className="carrusel-title">{title}</h2>
      </div>

      <div
        ref={containerRef}
        className="carrusel-container"
        style={{ '--vc': visibleCount }}  // → CSS usa este var para el ancho de cada card
        onContextMenu={preventContextMenu}
      >
        <button
          className="arrow left"
          onClick={handlePrev}
          disabled={!canPrev}
          aria-label="Anterior"
        >
          ‹
        </button>

        <div className="carrusel-track" role="list">
          {visibleItems.map((item, idx) => {
            if (item.type === 'more') {
              return (
                <div key="__more__" className="carrusel-item more" role="listitem">
                  <Link
                    to={categoryRoute}
                    className="more-bubble"
                    aria-label={`Ver más de ${title}`}
                  >
                    <span>Ver más</span>
                  </Link>
                </div>
              );
            }
            return (
              <div key={item.id || item.src || idx} className="carrusel-item" role="listitem">
                <Link to={item.detailRoute} aria-label={item.alt || 'Ver detalle'}>
                  <img
                    src={item.src}
                    alt={item.alt || ''}
                    loading="lazy"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                    onContextMenu={preventContextMenu}
                  />
                </Link>
              </div>
            );
          })}

          {/* Espacio blanco a la derecha cuando la visible es "Ver más" en 1× */}
          {showingMoreOneUp && <div className="carrusel-item spacer" aria-hidden="true" />}
        </div>

        <button
          className="arrow right"
          onClick={handleNext}
          disabled={!canNext}
          aria-label="Siguiente"
        >
          ›
        </button>
      </div>
    </section>
  );
}
